export const LOOT_RATE_DISPLAY_PREFERENCE_STORAGE_KEY =
  "fishystuff.loot-rate-display-preference.v1";
export const RETIRED_UI_SETTINGS_STORAGE_KEY =
  "fishystuff.ui.settings.v1";

const RETIRED_MODE_PATH = Object.freeze(["loot", "rateDisplayMode"]);
const MAX_RETIRED_SETTINGS_CHARS = 65_536;

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function result(kind, fields = {}) {
  return Object.freeze({ kind, ...fields });
}

function valueAtPath(root, path) {
  let current = root;
  for (const part of path) {
    if (!isRecord(current) || !Object.hasOwn(current, part)) {
      return result("missing");
    }
    current = current[part];
  }
  return result("found", { value: current });
}

function removePath(root, path) {
  if (!isRecord(root) || path.length === 0 || !Object.hasOwn(root, path[0])) {
    return root;
  }
  const next = { ...root };
  if (path.length === 1) {
    delete next[path[0]];
    return next;
  }
  const child = removePath(next[path[0]], path.slice(1));
  if (isRecord(child) && Object.keys(child).length > 0) {
    next[path[0]] = child;
  } else {
    delete next[path[0]];
  }
  return next;
}

function decodeRetiredSettings(raw) {
  if (typeof raw !== "string") {
    return result("invalid", {
      reason: "retired-ui-settings-must-be-text",
      raw,
    });
  }
  if (raw.length === 0 || raw.length > MAX_RETIRED_SETTINGS_CHARS) {
    return result("invalid", {
      reason: raw.length === 0
        ? "retired-ui-settings-must-not-be-empty"
        : "retired-ui-settings-exceeds-import-limit",
      raw,
    });
  }
  try {
    const settings = JSON.parse(raw);
    return isRecord(settings)
      ? result("ready", { raw, settings })
      : result("invalid", {
        reason: "retired-ui-settings-must-be-an-object",
        raw,
      });
  } catch (_error) {
    return result("invalid", {
      reason: "retired-ui-settings-is-malformed-json",
      raw,
    });
  }
}

function sameSettings(left, right) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (_error) {
    return false;
  }
}

export function createLocalLootRateDisplayPreferenceProvider({
  storage = null,
  eventTarget = null,
  legacySettings = null,
} = {}) {
  let disposed = false;
  let removingRetiredMode = false;
  const invalidationListeners = new Set();

  function unavailable(operation, reason = `storage-${operation}-failed`) {
    return result("unavailable", { operation, reason });
  }

  function readKey(key, operation = "read") {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return result("available", { raw: storage.getItem(key) });
    } catch (_error) {
      return unavailable(operation, "storage-read-failed");
    }
  }

  function writeKey(key, raw) {
    try {
      if (
        !storage
        || typeof storage.setItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(key, raw);
      if (storage.getItem(key) !== raw) {
        return unavailable("write", "storage-write-not-acknowledged");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable("write");
    }
  }

  function removeKey(key, operation = "remove") {
    try {
      if (
        !storage
        || typeof storage.removeItem !== "function"
        || typeof storage.getItem !== "function"
      ) {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(key);
      if (storage.getItem(key) !== null) {
        return unavailable(operation, "storage-remove-not-acknowledged");
      }
      return result("acknowledged");
    } catch (_error) {
      return unavailable(operation, "storage-remove-failed");
    }
  }

  function readDocument() {
    return readKey(LOOT_RATE_DISPLAY_PREFERENCE_STORAGE_KEY);
  }

  function writeDocument(raw) {
    return writeKey(LOOT_RATE_DISPLAY_PREFERENCE_STORAGE_KEY, raw);
  }

  function removeDocument() {
    return removeKey(LOOT_RATE_DISPLAY_PREFERENCE_STORAGE_KEY);
  }

  function readRetiredMode() {
    const stored = readKey(RETIRED_UI_SETTINGS_STORAGE_KEY, "legacy-read");
    if (stored.kind === "unavailable") return stored;
    if (stored.raw === null) return result("missing");
    const decoded = decodeRetiredSettings(stored.raw);
    if (decoded.kind === "invalid") return decoded;
    const candidate = valueAtPath(decoded.settings, RETIRED_MODE_PATH);
    return candidate.kind === "missing"
      ? candidate
      : result("candidate", {
        raw: decoded.raw,
        value: candidate.value,
      });
  }

  function removeRetiredMode() {
    const stored = readKey(RETIRED_UI_SETTINGS_STORAGE_KEY, "legacy-remove");
    if (stored.kind === "unavailable") return stored;
    if (stored.raw === null) return result("acknowledged");
    const decoded = decodeRetiredSettings(stored.raw);
    if (decoded.kind === "invalid") {
      return unavailable("legacy-remove", decoded.reason);
    }
    if (valueAtPath(decoded.settings, RETIRED_MODE_PATH).kind === "missing") {
      return result("acknowledged");
    }

    const expected = removePath(decoded.settings, RETIRED_MODE_PATH);
    if (
      typeof legacySettings?.remove === "function"
      && typeof legacySettings?.snapshot === "function"
    ) {
      if (!sameSettings(legacySettings.snapshot(), decoded.settings)) {
        return unavailable(
          "legacy-remove",
          "retired-ui-settings-cache-is-stale",
        );
      }
      try {
        removingRetiredMode = true;
        legacySettings.remove(
          RETIRED_MODE_PATH,
          "loot-rate-display-preference-import",
        );
      } catch (_error) {
        return unavailable(
          "legacy-remove",
          "retired-ui-settings-remove-failed",
        );
      } finally {
        removingRetiredMode = false;
      }
    } else if (Object.keys(expected).length === 0) {
      const removed = removeKey(
        RETIRED_UI_SETTINGS_STORAGE_KEY,
        "legacy-remove",
      );
      if (removed.kind === "unavailable") return removed;
    } else {
      const written = writeKey(
        RETIRED_UI_SETTINGS_STORAGE_KEY,
        JSON.stringify(expected),
      );
      if (written.kind === "unavailable") {
        return unavailable("legacy-remove", written.reason);
      }
    }

    const after = readKey(RETIRED_UI_SETTINGS_STORAGE_KEY, "legacy-remove");
    if (after.kind === "unavailable") return after;
    if (Object.keys(expected).length === 0) {
      return after.raw === null
        ? result("acknowledged")
        : unavailable(
          "legacy-remove",
          "retired-ui-settings-remove-not-acknowledged",
        );
    }
    if (after.raw === null) {
      return unavailable(
        "legacy-remove",
        "retired-ui-settings-siblings-were-removed",
      );
    }
    const decodedAfter = decodeRetiredSettings(after.raw);
    if (
      decodedAfter.kind !== "ready"
      || !sameSettings(decodedAfter.settings, expected)
    ) {
      return unavailable(
        "legacy-remove",
        "retired-ui-settings-remove-not-acknowledged",
      );
    }
    return result("acknowledged");
  }

  function notifyInvalidation() {
    if (disposed) return;
    for (const listener of invalidationListeners) {
      try {
        listener();
      } catch (error) {
        globalThis.reportError?.(error);
      }
    }
  }

  function handleStorage(event) {
    if (
      event?.key === null
      || event?.key === LOOT_RATE_DISPLAY_PREFERENCE_STORAGE_KEY
      || event?.key === RETIRED_UI_SETTINGS_STORAGE_KEY
    ) {
      notifyInvalidation();
    }
  }

  function handlePageShow(event) {
    if (event?.persisted === true) notifyInvalidation();
  }

  eventTarget?.addEventListener?.("storage", handleStorage);
  eventTarget?.addEventListener?.("pageshow", handlePageShow);
  const unsubscribeLegacy = typeof legacySettings?.subscribe === "function"
    ? legacySettings.subscribe(() => {
      if (!removingRetiredMode) notifyInvalidation();
    })
    : () => {};

  function subscribeInvalidation(listener) {
    if (typeof listener !== "function") {
      throw new TypeError(
        "Loot-rate display provider invalidation listener must be a function.",
      );
    }
    if (disposed) return () => {};
    invalidationListeners.add(listener);
    return () => invalidationListeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    invalidationListeners.clear();
    unsubscribeLegacy?.();
    eventTarget?.removeEventListener?.("storage", handleStorage);
    eventTarget?.removeEventListener?.("pageshow", handlePageShow);
  }

  return Object.freeze({
    dispose,
    readDocument,
    readRetiredMode,
    removeDocument,
    removeRetiredMode,
    subscribeInvalidation,
    writeDocument,
  });
}

export function createBrowserLocalLootRateDisplayPreferenceProvider() {
  let storage = null;
  try {
    storage = globalThis.localStorage ?? null;
  } catch (_error) {
    storage = null;
  }
  return createLocalLootRateDisplayPreferenceProvider({
    storage,
    eventTarget: globalThis.window ?? null,
    legacySettings: globalThis.window?.__fishystuffUiSettings ?? null,
  });
}
